import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Users2, Plus, X as XIcon, Camera, Upload } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const brl = (n) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(
    n || 0
  );

const dateBR = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};

export default function PublicRegistration() {
  const { competitionId } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const [form, setForm] = useState({
    nome: "",
    cpf: "",
    email: "",
    telefone: "",
    equipe: "",
    treinador: "",
    password: "",
    category_id: "",
    dobras: [], // list of { k: stable_key, v: category_id }
  });
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState("");
  const [photoError, setPhotoError] = useState("");

  const onPickPhoto = (e) => {
    setPhotoError("");
    const f = e.target.files?.[0];
    if (!f) {
      setPhotoFile(null);
      setPhotoPreview("");
      return;
    }
    if (!/^image\/(jpe?g|png|webp)$/i.test(f.type)) {
      setPhotoError("Envie um arquivo JPG, PNG ou WEBP.");
      return;
    }
    if (f.size > 3 * 1024 * 1024) {
      setPhotoError("Foto maior que 3 MB — comprima antes de enviar.");
      return;
    }
    setPhotoFile(f);
    const reader = new FileReader();
    reader.onload = (ev) => setPhotoPreview(ev.target?.result || "");
    reader.readAsDataURL(f);
  };
  const clearPhoto = () => {
    setPhotoFile(null);
    setPhotoPreview("");
    setPhotoError("");
  };

  useEffect(() => {
    let alive = true;
    axios
      .get(`${BACKEND}/api/public/competitions/${competitionId}/info`)
      .then((r) => alive && setInfo(r.data))
      .catch(
        (e) =>
          alive &&
          setError(e.response?.data?.detail || "Competição não encontrada")
      )
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [competitionId]);

  const loteAtual = info?.lote_atual;

  const total = useMemo(() => {
    if (!loteAtual) return 0;
    return (
      Number(loteAtual.valor_inscricao || 0) +
      form.dobras.filter((d) => d.v).length *
        Number(loteAtual.valor_dobra || 0)
    );
  }, [loteAtual, form.dobras]);

  const usedCatIds = useMemo(
    () => new Set([form.category_id, ...form.dobras.map((d) => d.v)].filter(Boolean)),
    [form.category_id, form.dobras]
  );

  const addDobra = () =>
    setForm({ ...form, dobras: [...form.dobras, { k: `d-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, v: "" }] });
  const removeDobra = (idx) =>
    setForm({ ...form, dobras: form.dobras.filter((_, i) => i !== idx) });
  const setDobra = (idx, value) => {
    const next = [...form.dobras];
    next[idx] = { ...next[idx], v: value };
    setForm({ ...form, dobras: next });
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const dobras = form.dobras.map((d) => d.v).filter((v) => v);
      if (new Set(dobras).size !== dobras.length) {
        throw new Error("Não repita a mesma categoria em duas dobras.");
      }
      if (dobras.some((d) => d === form.category_id)) {
        throw new Error(
          "A dobra deve ser em uma categoria diferente da principal."
        );
      }
      let fotoUrl = null;
      if (photoFile) {
        const fd = new FormData();
        fd.append("file", photoFile);
        const up = await axios.post(`${BACKEND}/api/public/upload-photo`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        fotoUrl = up.data?.path || null;
      }
      const payload = {
        competition_id: competitionId,
        nome: form.nome.trim(),
        cpf: form.cpf.trim(),
        email: form.email.trim(),
        telefone: form.telefone.trim(),
        equipe: form.equipe.trim(),
        treinador: form.treinador.trim(),
        password: form.password,
        category_id: form.category_id,
        dobra_category_ids: dobras,
        foto_url: fotoUrl,
      };
      const { data } = await axios.post(
        `${BACKEND}/api/public/register`,
        payload
      );
      setDone(data);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : err.message || "Erro ao inscrever.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading)
    return (
      <div className="min-h-screen text-white flex items-center justify-center">
        Carregando…
      </div>
    );
  if (error && !info)
    return (
      <div className="min-h-screen text-white flex items-center justify-center">
        <div className="card p-8 max-w-md text-center">
          <div className="text-[#C9A84C] font-display font-bold text-2xl mb-2">
            Ops
          </div>
          <div className="text-[var(--text-secondary)]">{error}</div>
        </div>
      </div>
    );

  const comp = info.competition;
  const cats = info.categories || [];
  const catById = Object.fromEntries(cats.map((c) => [c.id, c.nome]));
  const dobrasSelecionadas = form.dobras.map((d) => d.v).filter((v) => v);

  if (done) {
    const doneDobras = done.dobra_category_ids || (done.dobra_category_id ? [done.dobra_category_id] : []);
    return (
      <div className="min-h-screen text-white relative overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: 'url("/stage-bg.jpg")',
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            filter: "brightness(0.4) contrast(1.05) saturate(1.05)",
          }}
          aria-hidden="true"
        />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse at center, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.65) 55%, rgba(0,0,0,0.9) 100%)",
          }}
          aria-hidden="true"
        />
        <div className="max-w-3xl mx-auto px-6 py-14 relative z-10">
          <div className="card p-8 text-center" data-testid="registration-success">
            <CheckCircle2 size={56} className="mx-auto text-[#5AF78E] mb-4" />
            <h1 className="font-display font-black text-4xl tracking-tight mb-2">
              INSCRIÇÃO ENVIADA
            </h1>
            <p className="text-[var(--text-secondary)] mb-6">
              Sua pré-inscrição foi registrada. Aguarde a confirmação do organizador.
            </p>
            <div className="text-left border-t border-white/10 pt-6 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-[var(--text-secondary)]">Atleta</span>
                <b>{done.nome}</b>
              </div>
              <div className="flex justify-between">
                <span className="text-[var(--text-secondary)]">Lote</span>
                <b>{done.lote_nome}</b>
              </div>
              <div className="flex justify-between">
                <span className="text-[var(--text-secondary)]">Inscrição</span>
                <span className="tabular-nums">{brl(done.valor_inscricao)}</span>
              </div>
              {doneDobras.length > 0 && (
                <div className="flex justify-between">
                  <span className="text-[var(--text-secondary)]">
                    Dobras ({doneDobras.length})
                  </span>
                  <span className="tabular-nums">{brl(done.valor_dobra)}</span>
                </div>
              )}
              <div className="flex justify-between border-t border-white/10 pt-2 mt-2">
                <span className="font-bold">TOTAL A PAGAR</span>
                <b className="text-[#C9A84C] font-display text-xl tabular-nums">
                  {brl(done.valor_total)}
                </b>
              </div>
              <div className="text-xs text-[var(--text-secondary)] mt-3">
                Status:{" "}
                <b className="uppercase">
                  {done.status === "pending"
                    ? "Aguardando pagamento"
                    : done.status}
                </b>
                . Envie o comprovante de pagamento ao organizador via WhatsApp
                ou e-mail. Sua inscrição só é considerada oficial após a
                confirmação manual do organizador.
              </div>
            </div>
            <Link
              to="/login"
              data-testid="go-login-link"
              className="btn-primary mt-6 inline-block"
            >
              Acessar Minha Conta
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen text-white relative overflow-hidden">
      {/* Background do palco (mesmo do login) */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: 'url("/stage-bg.jpg")',
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          filter: "brightness(0.4) contrast(1.05) saturate(1.05)",
        }}
        aria-hidden="true"
      />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.65) 55%, rgba(0,0,0,0.9) 100%)",
        }}
        aria-hidden="true"
      />
      <div className="max-w-4xl mx-auto px-6 py-10 relative z-10">
        <div className="flex items-center gap-3 mb-8">
          <img
            src="/logo.jpg"
            alt="FITS4YOU"
            className="w-14 h-14 rounded-sm object-contain bg-white p-1"
          />
          <div>
            <div className="overline">Área do Atleta</div>
            <div className="font-display text-2xl font-black tracking-tight leading-none">
              INSCRIÇÃO ONLINE
            </div>
          </div>
        </div>

        <div className="card p-6 mb-6">
          <div className="overline mb-2">Competição</div>
          <div className="font-display text-3xl font-black tracking-tight">
            {comp.nome}
          </div>
          <div className="text-sm text-[var(--text-secondary)] mt-1">
            {dateBR(comp.data)} · {comp.local || "Local a definir"}
          </div>

          {loteAtual ? (
            <div
              className="mt-4 border border-[#C9A84C]/40 bg-[#C9A84C]/10 px-4 py-3"
              data-testid="lote-atual-box"
            >
              <div className="overline text-[#C9A84C]">Lote vigente</div>
              <div className="font-display font-black text-xl">
                {loteAtual.nome}
              </div>
              <div className="text-xs text-[var(--text-secondary)]">
                Válido de {dateBR(loteAtual.inicio)} até {dateBR(loteAtual.fim)}
              </div>
              <div className="mt-2 flex flex-wrap gap-6 text-sm">
                <div>
                  Inscrição:{" "}
                  <b className="tabular-nums">
                    {brl(loteAtual.valor_inscricao)}
                  </b>
                </div>
                <div>
                  Cada dobra:{" "}
                  <b className="tabular-nums">{brl(loteAtual.valor_dobra)}</b>
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-4 border border-[#FFCC00]/40 bg-[#FFCC00]/10 px-4 py-3 text-sm">
              O organizador ainda não configurou os lotes de inscrição. Volte
              em breve.
            </div>
          )}
        </div>

        <form
          onSubmit={submit}
          className="card p-6 space-y-5"
          data-testid="registration-form"
          autoComplete="off"
        >
          <div>
            <div className="overline mb-3 flex items-center gap-2">
              <Users2 size={14} /> Dados do atleta
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input
                data-testid="reg-nome"
                required
                className="input-field md:col-span-2"
                placeholder="Nome completo"
                autoComplete="off"
                name="reg-nome"
                value={form.nome}
                onChange={(e) => setForm({ ...form, nome: e.target.value })}
              />
              <input
                data-testid="reg-cpf"
                required
                className="input-field"
                placeholder="CPF"
                autoComplete="off"
                name="reg-cpf"
                value={form.cpf}
                onChange={(e) => setForm({ ...form, cpf: e.target.value })}
              />
              <input
                data-testid="reg-tel"
                className="input-field"
                placeholder="Telefone / WhatsApp"
                autoComplete="off"
                name="reg-telefone"
                value={form.telefone}
                onChange={(e) =>
                  setForm({ ...form, telefone: e.target.value })
                }
              />
              <input
                data-testid="reg-email"
                required
                type="email"
                className="input-field"
                placeholder="E-mail (será seu login)"
                autoComplete="off"
                name="reg-email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
              <input
                data-testid="reg-password"
                required
                type="password"
                minLength={6}
                className="input-field"
                placeholder="Crie uma senha (mín. 6 caracteres)"
                autoComplete="new-password"
                name="reg-new-password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
              />
              <input
                data-testid="reg-equipe"
                className="input-field"
                placeholder="Nome do Time / Equipe"
                autoComplete="off"
                name="reg-equipe"
                value={form.equipe}
                onChange={(e) => setForm({ ...form, equipe: e.target.value })}
              />
              <input
                data-testid="reg-treinador"
                className="input-field md:col-span-2"
                placeholder="Nome do Treinador"
                autoComplete="off"
                name="reg-treinador"
                value={form.treinador}
                onChange={(e) => setForm({ ...form, treinador: e.target.value })}
              />
            </div>

            <div className="mt-4 border border-white/10 rounded-sm p-4 bg-white/[0.02]">
              <div className="flex items-center gap-2 mb-2 text-sm">
                <Camera size={16} style={{ color: "#C9A84C" }} />
                <span className="overline">Foto do atleta (opcional)</span>
              </div>
              <div className="text-[11px] text-[var(--text-secondary)] mb-3">
                Aparece no Monitor de Backstage durante o evento. JPG, PNG ou WEBP até 3 MB.
              </div>
              <div className="flex items-center gap-4">
                {photoPreview ? (
                  <img
                    src={photoPreview}
                    alt="Prévia"
                    className="w-20 h-20 rounded-full object-cover border-2"
                    style={{ borderColor: "#C9A84C" }}
                    data-testid="reg-photo-preview"
                  />
                ) : (
                  <div
                    className="w-20 h-20 rounded-full flex items-center justify-center text-white/40"
                    style={{ background: "#111", border: "1px dashed rgba(255,255,255,0.2)" }}
                  >
                    <Camera size={22} />
                  </div>
                )}
                <div className="flex-1 flex flex-col gap-2">
                  <label
                    className="btn-ghost flex items-center gap-2 cursor-pointer w-fit"
                    data-testid="reg-photo-pick"
                  >
                    <Upload size={14} />
                    {photoFile ? "Trocar foto" : "Escolher foto"}
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={onPickPhoto}
                      className="hidden"
                    />
                  </label>
                  {photoFile && (
                    <button
                      type="button"
                      onClick={clearPhoto}
                      className="text-xs text-white/50 hover:text-white/80 w-fit"
                      data-testid="reg-photo-clear"
                    >
                      Remover foto
                    </button>
                  )}
                  {photoError && (
                    <div className="text-xs text-[#C9A84C]" data-testid="reg-photo-error">
                      {photoError}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div>
            <div className="overline mb-3">Categoria principal</div>
            <select
              data-testid="reg-cat-principal"
              required
              className="input-field"
              value={form.category_id}
              onChange={(e) =>
                setForm({ ...form, category_id: e.target.value })
              }
            >
              <option value="">Selecione a categoria principal</option>
              {cats.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nome}
                </option>
              ))}
            </select>
          </div>

          <div>
            <div className="overline mb-3 flex items-center justify-between">
              <span>
                Dobras — categorias adicionais{" "}
                <span className="text-[var(--text-secondary)] normal-case tracking-normal">
                  ({brl(loteAtual?.valor_dobra)} cada)
                </span>
              </span>
              <button
                type="button"
                data-testid="reg-add-dobra"
                className="btn-ghost !py-1 !px-2 inline-flex items-center gap-1 text-xs"
                onClick={addDobra}
                disabled={!form.category_id || form.dobras.length >= cats.length - 1}
              >
                <Plus size={12} /> Adicionar dobra
              </button>
            </div>

            {form.dobras.length === 0 && (
              <div className="text-xs text-[var(--text-secondary)] border border-white/10 px-3 py-3 border-dashed">
                Nenhuma dobra. Clique em <b>Adicionar dobra</b> para incluir
                categorias adicionais.
              </div>
            )}

            <div className="space-y-2">
              {form.dobras.map((row, idx) => (
                <div
                  key={row.k}
                  className="flex items-center gap-2"
                  data-testid={`reg-dobra-row-${idx}`}
                >
                  <select
                    data-testid={`reg-dobra-select-${idx}`}
                    required
                    className="input-field flex-1"
                    value={row.v}
                    onChange={(e) => setDobra(idx, e.target.value)}
                  >
                    <option value="">Selecione a categoria da dobra {idx + 1}</option>
                    {cats
                      .filter(
                        (c) => !usedCatIds.has(c.id) || c.id === row.v
                      )
                      .map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.nome}
                        </option>
                      ))}
                  </select>
                  <button
                    type="button"
                    data-testid={`reg-dobra-remove-${idx}`}
                    className="btn-ghost !p-2"
                    onClick={() => removeDobra(idx)}
                    aria-label="Remover dobra"
                  >
                    <XIcon size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="border-t border-white/10 pt-5">
            <div className="overline mb-3">Resumo do pagamento</div>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span>Inscrição ({loteAtual?.nome || "—"})</span>
                <span className="tabular-nums">
                  {brl(loteAtual?.valor_inscricao)}
                </span>
              </div>
              {dobrasSelecionadas.map((d, idx) => (
                <div className="flex justify-between" key={d || `sum-${idx}`}>
                  <span>
                    Dobra {idx + 1}: {catById[d] || "—"}
                  </span>
                  <span className="tabular-nums">
                    {brl(loteAtual?.valor_dobra)}
                  </span>
                </div>
              ))}
              <div className="flex justify-between border-t border-white/10 pt-2 mt-2 font-display text-2xl">
                <span>TOTAL</span>
                <b
                  data-testid="reg-total"
                  className="text-[#C9A84C] tabular-nums"
                >
                  {brl(total)}
                </b>
              </div>
            </div>
          </div>

          {error && (
            <div
              data-testid="reg-error"
              className="text-sm text-[#C9A84C] border border-[#C9A84C]/30 bg-[#C9A84C]/10 px-3 py-2"
            >
              {error}
            </div>
          )}

          <button
            data-testid="reg-submit"
            type="submit"
            className="btn-primary w-full"
            disabled={submitting || !loteAtual}
          >
            {submitting ? "Enviando..." : "CONFIRMAR INSCRIÇÃO"}
          </button>
          <div className="text-center text-xs text-[var(--text-secondary)]">
            Já tem conta?{" "}
            <Link to="/login" className="underline">
              Entrar
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
